/**
 * `$api` — one configured `$fetch` every request goes through.
 *
 * ITS PATH IS A PLAIN `string`, AND EVERY CALLER STATES WHAT IT GETS BACK (MAR-1131). `$fetch` and
 * `$fetch.create()` look each call's path up in the route map Nuxt generates into
 * `.nuxt/types/nitro-routes.d.ts`. That lookup costs TypeScript instantiations in proportion to the
 * number of routes, on every call, and with ~280 routes a single call sat at 99% of the compiler's
 * per-expression limit: adding two routes tipped it into `TS2589` and failed the typecheck. So `$api`
 * is typed below as `Api` — a flat `string` path and a caller-supplied `<T>` that defaults to
 * `unknown` — and the route map is never consulted. Write `$api<Thing>(path)` with `Thing` imported
 * from `#shared/<domain>/<name>.ts`, the same type the service function is annotated with
 * (apps/web/AGENTS.md, "API and plumbing"). An unannotated call returns `unknown` and will not
 * compile where its result is used, which is the point.
 *
 * Do not add an absolute `baseURL`: the API is same-origin.
 *
 * WHY A PLUGIN AND NOT A WRAPPER FUNCTION: the behaviour is expressed as ofetch interceptors, which
 * is what interceptors are for:
 *
 *   - Bearer token on the client, forwarded cookies on SSR
 *   - 503 tracking for the global degraded banner
 *   - the usage-limit handler
 *   - errors re-thrown, so callers can still handle them
 *
 * `apiStreamFetch` deliberately stays in `useApi()`: it returns a raw `Response` for streaming and
 * has no parsed payload to type.
 */
import type { Auth } from 'firebase/auth'
import type { FetchOptions } from 'ofetch'
import { ORG_OVERRIDE_HEADER } from '#shared/organizations/orgOverride'
import { API_ERROR_CODES, apiErrorCode } from '#shared/apiErrorCodes'
import { APP_CHECK_HEADER_NAME, APP_CHECK_REQUIRED_CODE, appCheckTokenKindFor } from '#shared/appCheck'
import { appCheckTokenFor } from '~/utils/appCheckToken'
import { getStoredOrgOverride, clearStoredOrgOverride } from '~/utils/orgOverride'
import { CLAIMS_REFRESHED_HEADER } from '#shared/organizations/identityClaimsWire'

/**
 * `$api`'s call signature. Deliberately not `$fetch`'s: see the header.
 *
 * The options are ofetch's `FetchOptions`, NOT `NitroFetchOptions<string>`. `NitroFetchOptions`
 * extends `FetchOptions` only to narrow `method` per route, and its default method parameter,
 * `AvailableRouterMethod<string>`, matches `string` against every route in the map — the same
 * per-route cost this type exists to remove. Measured: with two more routes it alone raised
 * `TS2589` on this line. `FetchOptions` keeps `method`, `body`, `query` and `responseType` typed.
 */
export type Api = <T = unknown>(request: string, opts?: FetchOptions) => Promise<T>

export default defineNuxtPlugin((nuxtApp) => {
  const { recordFailure, recordSuccess } = useServiceStatus()
  const { handleResponseError } = useUsageLimit()

  /**
   * Pull a fresh ID token after the server re-minted this user's identity claims (MAR-427).
   *
   * WITHOUT THIS THE CLAIMS ARRIVE UP TO AN HOUR LATE, and the gap is user-visible in the worst
   * place: a user who has just created their organization would keep presenting a token that says
   * they belong to none, and every request would 403 `NO_ORGANIZATION` until Firebase refreshed on
   * its own schedule.
   *
   * `getIdToken(true)` fires `onIdTokenChanged`, which re-posts the `__session` cookie
   * (`app/plugins/firebase.client.ts`), so both carriers of the token move together. It cannot loop:
   * the refreshed token carries the claims, so the server has nothing left to re-mint and stops
   * setting the header.
   */
  async function refreshIdToken() {
    try {
      const auth = nuxtApp.$firebaseAuth as Auth | undefined
      await auth?.currentUser?.getIdToken(true)
    } catch (err) {
      // The next hourly refresh picks the claims up; the server's fallback serves us until then.
      console.warn('[auth] failed to refresh ID token after a claims update:', err)
    }
  }

  /**
   * Cast through `unknown` on purpose. Letting TypeScript check `$Fetch` against `Api` makes it
   * compare the route-map generic structurally, which exceeds its stack depth (`TS2321`). The two
   * are the same function at runtime; `Api` only drops the route lookup from the type.
   */
  const baseApi = $fetch.create({
    async onRequest({ request, options }) {
      const headers = new Headers(options.headers)

      if (import.meta.server) {
        // SSR runs inside the incoming request, so the browser's session cookie is forwarded rather
        // than re-minted.
        const { cookie } = useRequestHeaders(['cookie'])
        if (cookie) headers.set('cookie', cookie)
      } else {
        if (!headers.has('Authorization')) {
          const { getToken } = useAuth()
          const token = await getToken()
          if (token) headers.set('Authorization', `Bearer ${token}`)
        }
        // Only the gated routes carry an App Check token (MAR-1214): asking for one initializes App
        // Check, which costs a reCAPTCHA assessment, so an ordinary page must never ask.
        const tokenKind = typeof request === 'string' ? appCheckTokenKindFor(options.method, request) : null
        if (tokenKind && !headers.has(APP_CHECK_HEADER_NAME)) {
          const appCheckToken = await appCheckTokenFor(nuxtApp, tokenKind)
          if (appCheckToken) headers.set(APP_CHECK_HEADER_NAME, appCheckToken)
        }
        // The super-admin org override, stated per request rather than carried ambiently in a
        // cookie (MAR-427). SSR deliberately does not send it: a page render carries only cookies,
        // so the SSR-seeded profile is always the real org's and `app/layouts/default.vue`
        // reconciles it after hydration when this value disagrees with it (MAR-475).
        const orgOverride = getStoredOrgOverride()
        if (orgOverride) headers.set(ORG_OVERRIDE_HEADER, orgOverride)
      }

      options.headers = headers
    },

    async onResponse({ response }) {
      if (response.ok) recordSuccess()
      if (import.meta.client && response.headers.get(CLAIMS_REFRESHED_HEADER) === '1') {
        await refreshIdToken()
      }
    },

    onResponseError({ response }) {
      // 503 is the one status that means the service, not the request — it drives the degraded
      // banner. Everything else is the caller's problem and is re-thrown untouched.
      if (response.status === 503) recordFailure()
      // A refused override would otherwise refuse EVERY subsequent request, because the header rides
      // along on all of them. The server used to clear its own cookie; the client owns the value now,
      // so dropping it here is the equivalent step.
      if (response._data?.data?.code === API_ERROR_CODES.orgOverrideInvalid) clearStoredOrgOverride()
      /**
       * `MFA_REQUIRED` (AlexAmin/marcus#899) — an already-open session (the `__session` cookie, or an
       * Authorization header built from a cached ID token) that predates the user activating
       * two-factor auth, or that never completed a second factor. `app/plugins/firebase.client.ts`'s
       * `onIdTokenChanged` handles the same code for the token-refresh path; this covers every
       * ORDINARY API call, which is the path most of the app actually takes. Fire-and-forget: signing
       * out is a hard navigation (`window.location.href = '/login'`), so nothing here needs to await
       * it before letting the original error continue to whatever called `apiFetch`.
       */
      if (response._data?.data?.code === API_ERROR_CODES.mfaRequired) void useAuth().signOut()
      if (import.meta.client && response._data?.data?.code === API_ERROR_CODES.mfaSetupRequired) {
        const { profile } = useUserProfile()
        if (profile.value) profile.value.organization_require_mfa = true
        if (window.location.pathname.replace(/\/+$/, '') !== '/app/settings/security') {
          void navigateTo('/app/settings/security')
        }
      }
      handleResponseError(response.status, response._data?.data)
    },
  }) as unknown as Api

  /**
   * One retry with an App Check token after a 401 `app_check_required` (MAR-1214). This is how a
   * brand-new account's first request gets its token: the server demands one only before it writes
   * that account's first `users` row (`resolveUserContext`), and the client cannot know which
   * request that will be, so it waits to be asked rather than attesting on every call. A request that
   * already carried a token is not retried — the refusal was about the token itself.
   */
  const api: Api = async <T>(request: string, opts?: FetchOptions): Promise<T> => {
    try {
      return await baseApi<T>(request, opts)
    } catch (err) {
      if (!import.meta.client || (apiErrorCode(err) as string | null) !== APP_CHECK_REQUIRED_CODE) throw err
      if (new Headers(opts?.headers).has(APP_CHECK_HEADER_NAME)) throw err
      const kind = appCheckTokenKindFor(opts?.method, request) ?? 'standard'
      const token = await appCheckTokenFor(nuxtApp, kind)
      if (!token) throw err
      const headers = new Headers(opts?.headers)
      headers.set(APP_CHECK_HEADER_NAME, token)
      return await baseApi<T>(request, { ...opts, headers })
    }
  }

  return { provide: { api } }
})
